import { IAroundAttraction } from "@/components/organisms/tourist-attraction-detail/AroundAttraction";
import {
  HomeBrand,
  HomeBrandsBottomListDatasourcesCrmBottomListDatasourceBrandDetail,
  HomeMeetingsAndBanquetsDatasourcesHeroBannerDatasource,
  JapanArea,
  MeetingsAndBanquetsDatasourceIntroductionDatasourcesIntroduction,
  UploadFile,
  Maybe,
  JapanHotel,
} from "@/types/generated";
import { ISelectionResponse } from "@/types/mice";

export interface MapBrandIcon {
  documentId: string;
  icon: UploadFile;
  itemName?: string;
}

export const mapHeroBannerData = (
  detailsImage: UploadFile[],
  title: string,
) => {
  if (!detailsImage || !title)
    return {} as HomeMeetingsAndBanquetsDatasourcesHeroBannerDatasource;
  return {
    information: {
      title,
      background_image: detailsImage,
    },
  } as HomeMeetingsAndBanquetsDatasourcesHeroBannerDatasource;
};

export const findAreaDetail = (
  attractionDetailResponse: JapanArea[],
  citySlug: string,
) => {
  if (!attractionDetailResponse || !citySlug) return {} as JapanArea;
  const findWithCitySlug = attractionDetailResponse.find(
    (item) => item?.item_name?.toLocaleLowerCase() === citySlug,
  );
  return findWithCitySlug as JapanArea;
};

export const findAttractionDetail = (
  attractionDetailResponse: JapanArea[],
  itemName: string,
  citySlug: string,
  slug: string,
  pathName: string,
) => {
  if (!attractionDetailResponse || !itemName || !citySlug)
    return {} as IAroundAttraction;
  const findWithCitySlug: JapanArea = findAreaDetail(
    attractionDetailResponse,
    citySlug,
  );
  return {
    ...findWithCitySlug?.attractions?.find(
      (item) =>
        item?.slug === pathName?.replace("/", "") || item?.slug?.includes(slug),
    ),
    area_name: findWithCitySlug?.area_name,
    area_title: findWithCitySlug?.title,
    slug_attraction: findWithCitySlug?.slug_attraction,
    cover_image: findWithCitySlug?.cover_image,
  } as IAroundAttraction;
};

export const mapIntroductionData = (title: string, introduction: string) => {
  if (!title || !introduction)
    return {} as MeetingsAndBanquetsDatasourceIntroductionDatasourcesIntroduction;
  return {
    properties: {
      title,
      introduction,
    },
  } as MeetingsAndBanquetsDatasourceIntroductionDatasourcesIntroduction;
};

export const mapAroundAttractionsData = (
  japanAreas: JapanArea[],
  selectedAttraction: ISelectionResponse[],
  citySlug: string,
) => {
  if (!japanAreas || !selectedAttraction || !citySlug)
    return [] as IAroundAttraction[];

  const mapWithSelected = japanAreas?.flatMap((item) =>
    item?.attractions?.map((attraction) => ({
      ...attraction,
      area_name: item?.area_name,
      area_title: item?.title,
    })),
  );

  const attractionMap = new Map(
    mapWithSelected?.map((item) => [item?.documentId, item]),
  );
  const sortedArray = [...selectedAttraction]
    ?.sort((a, b) => a.order - b.order)
    ?.map((selected) => attractionMap?.get(selected?.documentId))
    ?.filter(Boolean);

  return sortedArray as IAroundAttraction[];
};

const processTopListDatasources = (data: HomeBrand): MapBrandIcon[] => {
  const result: MapBrandIcon[] = [];
  const topList =
    data.top_list_datasources?.[0]
      ?.home_brands_top_list_datasources_crm_top_list_datasource_top_details;

  if (!topList) return result;

  for (const item of topList) {
    if (item) {
      result.push({
        itemName: item.item_name,
        documentId: item.brand_url?.metadata?.documentId,
        icon: item.icon || ({} as UploadFile),
      });
    }
  }
  return result;
};

const processMiddleListDatasources = (data: HomeBrand): MapBrandIcon[] => {
  const result: MapBrandIcon[] = [];
  if (!data.middle_list_datasources) return result;

  for (const item of data.middle_list_datasources){
    if (item) {
      result.push({
        itemName: item.item_name,
        documentId: item.hotel_button_link?.metadata?.documentId,
        icon: item.icon || ({} as UploadFile),
      });
    }
  }
  return result;
};

const processBottomListDatasources = (data: HomeBrand): MapBrandIcon[] => {
  const result: MapBrandIcon[] = [];
  const bottomList = data.bottom_list_datasources?.[0]
    ?.brand_details as Maybe<HomeBrandsBottomListDatasourcesCrmBottomListDatasourceBrandDetail>[];

  if (!bottomList) return result;

  for (const item of bottomList) {
    if (item) {
      result.push({
        itemName: item.item_name,
        documentId: item.brand_link?.metadata?.documentId,
        icon: item.icon || ({} as UploadFile),
      });
    }
  }
  return result;
};

export const mapBrandIcons = (data: HomeBrand) => {
  return [
    ...processTopListDatasources(data),
    ...processMiddleListDatasources(data),
    ...processBottomListDatasources(data),
  ];
};

export const getAreaHotelThumbnailUrl = (
  hotel: JapanHotel | null | undefined,
): UploadFile | undefined => {
  if (!hotel) return undefined;

  const imageId = hotel?.datasources
    ? hotel?.datasources[0]?.slider_datasources[0]?.information
        ?.picked_images_desktop[0]?.documentId
    : "";
  if (!imageId) return undefined;

  const imageUrl = hotel?.hotel_images?.slider_desktop?.find(
    (hotelImage) => hotelImage?.documentId === imageId,
  )?.image_url;

  return imageUrl || undefined;
};

export const getAreaHotelThumbnail = (
  hotel: JapanHotel | null | undefined,
): UploadFile => {
  if (!hotel) return {} as UploadFile;

  const imageId = hotel?.datasources
    ? hotel?.datasources[0]?.slider_datasources[0]?.information
        ?.picked_images_desktop[0]?.documentId
    : "";
  if (!imageId) return {} as UploadFile;

  const imageUrl = hotel?.hotel_images?.slider_desktop?.find(
    (hotelImage) => hotelImage?.documentId === imageId,
  )?.image_url;

  return (imageUrl || {}) as UploadFile;
};

export const getSpecialAreaName = (name: string) => {
  const specialArea = ["北海道", "九州", "関西"];
  if (!name) return "";
  if (specialArea.includes(name)) {
    switch (name) {
      case "北海道":
        return "北海道";
      case "九州":
        return "九州・沖縄地方";
      case "関西":
        return "関西地方";
      default:
        return name + "地方";
    }
  }
  return name + "地方";
};